import React, { useEffect, useMemo, useState, Key } from 'react';
import { Row, Col, Button, message, Select } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import InternalControlLayout from 'components/Layouts/InternalControlLayout';
import ApiService from 'utils/ApiCalls';
import { Entity, ProcessParentType } from 'store/ducks/InternalControls/types';
import { RootState } from 'store/configureStore';
import { internalControlsOp } from 'store/ducks/InternalControls';
import Helpers from 'utils/Helpers';
import * as Styled from './ProcessAllocation.styled';

const { Option } = Select;
const ProcessAllocation: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { scheme } = useSelector((state: RootState) => ({
    scheme: state.global.scheme
  }));

  const [processes, setProcesses] = useState<Entity[]>([]);
  const [groups, setGroups] = useState<Entity[]>([]);
  const [divisions, setDivisions] = useState<Entity[]>([]);
  const [regions, setRegions] = useState<Entity[]>([]);
  const [legalEntities, setLegalEntities] = useState<Entity[]>([]);
  const [businessUnits, setBusinessUnits] = useState<Entity[]>([]);
  const [leftSideProcesses, setLeftSideProcesses] = useState<Entity[]>([]);
  const [allProcesses, setAllProcesses] = useState<Entity[]>([]);
  const [loadingAllProcesses, setLoadingAllProcesses] = useState<boolean>(true);

  const [selectedParents, setSelectedParents] = useState<
    Record<ProcessParentType, number | undefined>
  >({} as Record<ProcessParentType, number | undefined>);

  const [targets, setTargets] = useState<Record<ProcessParentType, Key[]>>(
    {} as Record<ProcessParentType, Key[]>
  );

  const [initialTargets, setInitialTargets] = useState<
    Record<ProcessParentType, Key[]>
  >({} as Record<ProcessParentType, Key[]>);

  const [loading, setLoading] = useState<Record<ProcessParentType, boolean>>(
    {} as Record<ProcessParentType, boolean>
  );

  const [allocatedProcessesMap, setAllocatedProcessesMap] = useState<
    Record<ProcessParentType, Entity[]>
  >({} as Record<ProcessParentType, Entity[]>);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [g, d, r, le, bu, pr] = await Promise.all([
          ApiService.getGroupBPR(),
          ApiService.getDivisionBPR(),
          ApiService.getRegionBPR(),
          ApiService.getLegalEntitiesBPR(),
          ApiService.getBusinessUnitBPR(),
          ApiService.getBusinessProcessL4BPR()
        ]);

        setGroups(g.data.data);
        setDivisions(d.data.data);
        setRegions(r.data.data);
        setLegalEntities(le.data.data);
        setBusinessUnits(bu.data.data);
        setProcesses(pr.data.data);
      } catch {
        message.error('Failed to load allocation data');
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      try {
        setLoadingAllProcesses(true);
        const res = await ApiService.getBusinessProcessL4BPR();
        setAllProcesses(res.data.data);
      } catch {
        message.error('Failed to load processes');
      } finally {
        setLoadingAllProcesses(false);
      }
    };
    loadAll();
  }, []);

  const loadAllocatedProcesses = async (
    parentType: ProcessParentType,
    parentId: number
  ) => {
    try {
      const params: Record<string, number> = {};
      switch (parentType) {
        case 'group':
          params.group_id = parentId;
          break;
        case 'division':
          params.division_id = parentId;
          break;
        case 'region':
          params.region_id = parentId;
          break;
        case 'legal_entity':
          params.legal_entity_id = parentId;
          break;
        case 'business_unit':
          params.business_unit_id = parentId;
          break;
      }

      const res = await ApiService.getBusinessProcessL4BPR(params);
      const allocated: Entity[] = res.data.data;

      const keys = allocated.map((p) => `process-${p.id}`);
      setTargets((prev) => ({ ...prev, [parentType]: keys }));
      setInitialTargets((prev) => ({ ...prev, [parentType]: keys }));

      setAllocatedProcessesMap((prev) => ({
        ...prev,
        [parentType]: allocated
      }));
    } catch {
      message.error('Failed to load allocated processes');
    }
  };

  const parentCollections = {
    group: groups,
    division: divisions,
    region: regions,
    legal_entity: legalEntities,
    business_unit: businessUnits
  };

  const handleSave = async (parentType: ProcessParentType) => {
    if (!selectedParents[parentType]) {
      message.warning('Please select a parent first');
      return;
    }

    try {
      setLoading((p) => ({ ...p, [parentType]: true }));

      const keys = targets[parentType] || [];
      const allToSave = [
        ...leftSideProcesses,
        ...allProcesses.filter((p) => keys.includes(`process-${p.id}`))
      ];

      const payload = {
        data_list: allToSave.map((p) => {
          const key = `process-${p.id}`;
          return {
            id: p.id,
            title: p.title,
            description: p.description,
            description_1: p.description_1,
            description_2: p.description_2,
            activation_date: p.activation_date,
            start_date: p.start_date,
            end_date: p.end_date,
            comments: p.comments,
            owner_maintainer: p.owner_maintainer,
            responsible_person: p.responsible_person,
            process_image: p.process_image,
            process_risk: p.process_risk,

            group_id: targets['group']?.includes(key)
              ? selectedParents['group'] ?? null
              : null,
            division_id: targets['division']?.includes(key)
              ? selectedParents['division'] ?? null
              : null,
            region_id: targets['region']?.includes(key)
              ? selectedParents['region'] ?? null
              : null,
            legal_entity_id: targets['legal_entity']?.includes(key)
              ? selectedParents['legal_entity'] ?? null
              : null,
            business_unit_id: targets['business_unit']?.includes(key)
              ? selectedParents['business_unit'] ?? null
              : null,

            process_type_id: p.process_type?.id ?? null,
            process_criticality_id: p.process_criticality?.id ?? null,
            business_process_l3_id: p.business_process_l3?.id ?? null
          };
        })
      };

      await ApiService.saveProcessBPR(payload);

      const refreshed = await ApiService.getBusinessProcessL4BPR();
      setProcesses(refreshed.data.data);
      setAllProcesses(refreshed.data.data);

      const parentTypes: ProcessParentType[] = [
        'group',
        'division',
        'region',
        'legal_entity',
        'business_unit'
      ];

      for (const type of parentTypes) {
        const parentId = selectedParents[type];
        if (parentId) {
          await loadAllocatedProcesses(type, parentId);
        }
      }

      dispatch(internalControlsOp.setHasChanges(false));
      message.success('Allocation saved successfully');
    } catch (e: any) {
      message.error(e?.message || 'Save failed');
    } finally {
      setLoading((p) => ({ ...p, [parentType]: false }));
    }
  };

  const configs: {
    type: ProcessParentType;
    title: string;
    parents: Entity[];
  }[] = [
    {
      type: 'group',
      title: t('ALLOCATE_PROCESSES_GROUP'),
      parents: [...groups].sort((a, b) => a.name.localeCompare(b.name))
    },
    {
      type: 'division',
      title: t('ALLOCATE_PROCESSES_DIVISION'),
      parents: [...divisions].sort((a, b) => a.name.localeCompare(b.name))
    },
    {
      type: 'region',
      title: t('ALLOCATE_PROCESSES_REGION'),
      parents: [...regions].sort((a, b) => a.name.localeCompare(b.name))
    },
    {
      type: 'legal_entity',
      title: t('ALLOCATE_PROCESSES_LEGAL_ENTITY'),
      parents: [...legalEntities].sort((a, b) => a.name.localeCompare(b.name))
    },
    {
      type: 'business_unit',
      title: t('ALLOCATE_PROCESSES_BUSINESS_UNIT'),
      parents: [...businessUnits].sort((a, b) => a.name.localeCompare(b.name))
    }
  ];

  // Update global hasChanges state when local changes occur
  useEffect(() => {
    const hasAnyChanges = configs.some(({ type }) => {
      return (
        selectedParents[type] &&
        !Helpers.areKeysEqual(targets[type], initialTargets[type])
      );
    });
    dispatch(internalControlsOp.setHasChanges(hasAnyChanges));
  }, [targets, initialTargets, selectedParents, dispatch]);

  return (
    <InternalControlLayout>
      <Styled.ContentWrapper>
        <Row gutter={[24, 24]}>
          {configs.map(({ type, title, parents }) => {
            const hasChanges =
              selectedParents[type] &&
              !Helpers.areKeysEqual(targets[type], initialTargets[type]);
            const count = (targets[type] || []).length;

            return (
              <Col xs={24} sm={24} md={12} lg={12} xl={12} key={type}>
                <Styled.TransferCard
                  scheme={scheme}
                  title={title}
                  extra={
                    <Button
                      type='primary'
                      disabled={!hasChanges}
                      loading={loading[type]}
                      onClick={() => handleSave(type)}
                    >
                      {t('SAVE')}
                    </Button>
                  }
                >
                  <Styled.SelectParent
                    placeholder='Select parent'
                    disabled={loadingAllProcesses}
                    onChange={async (value) => {
                      const parentId = value as number;
                      setSelectedParents((p) => ({ ...p, [type]: parentId }));

                      await loadAllocatedProcesses(type, parentId);
                    }}
                  >
                    {parents.map((p) => (
                      <Option key={p.id} value={p.id}>
                        {p.name}
                      </Option>
                    ))}
                  </Styled.SelectParent>
                  <Styled.ProcessTransferCards
                    dataSource={[
                      ...allProcesses.map((p) => ({
                        key: `process-${p.id}`,
                        title: p.title || p.name
                      })),
                      ...(allocatedProcessesMap[type]?.map((p) => ({
                        key: `process-${p.id}`,
                        title: p.title || p.name
                      })) || [])
                    ]}
                    targetKeys={targets[type] || []}
                    titles={[
                      'Processes',
                      count > 0
                        ? `${count} Process${
                            count !== 1 ? 'es' : ''
                          } added to ${type
                            .replace('_', ' ')
                            .replace(/\b\w/g, (c) => c.toUpperCase())}`
                        : ''
                    ]}
                    onChange={(keys) => {
                      setTargets((p) => ({ ...p, [type]: keys }));

                      const combinedProcesses = [
                        ...allProcesses,
                        ...(allocatedProcessesMap[type] || [])
                      ];

                      const uniqueProcesses = combinedProcesses.filter(
                        (v, i, a) => a.findIndex((p) => p.id === v.id) === i
                      );

                      const leftSide = uniqueProcesses.filter(
                        (p) => !keys.includes(`process-${p.id}`)
                      );
                      setLeftSideProcesses(leftSide);

                      const rightSide = uniqueProcesses.filter((p) =>
                        keys.includes(`process-${p.id}`)
                      );
                    }}
                    render={(item) => <span>{item.title}</span>}
                    showSearch
                    disabled={!selectedParents[type]}
                    filterOption={(input, option) =>
                      String(option.title)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Styled.TransferCard>
              </Col>
            );
          })}
        </Row>
      </Styled.ContentWrapper>
    </InternalControlLayout>
  );
};

export default ProcessAllocation;
